import { createPortal } from "react-dom";
import { useState, useEffect } from "react";
import { Image, Button } from "react-bootstrap";
import ChatbotWindow from "./ChatbotWindow";
import Logo from "Images/PSOPPC-logo-only.png";
import { useLocation } from "react-router-dom";
import { PATHS } from "Utils/PagePaths";
import { buildTimestamp, WELCOME_MESSAGE } from "./ChatbotUtils";
import { IconButton } from 'Components/Button';
import { ICONS } from 'Utils/icons';
import CloseIcon from '@mui/icons-material/Close';
import { chatbotHealthCheck } from "./ChatbotAxiosFuncs";

const Chatbot = ({ zIndex = 500, suggestionInView }) => {

  const SUGGESTION_CONTAIONER_BOTTOM = 180;
  const SUGGESTION_MESSAGE_LIST = [
    "How do I become a PSO?",
    "What is AHRQ Common Formats?",
    "How do I submit patient safety data?"
  ];

  const resetChatbotChannel = new BroadcastChannel("Reset_Chatbot");
  const location = useLocation();

  const [show, setShow] = useState(false);
  const [showSuggestion, setShowSuggestion] = useState(location.pathname === PATHS.public["Homepage"] ? true : false);
  const [closeSuggestionByUser, setCloseSuggestionByUser] = useState(false);
  const [isChatbotHealthy, setIsChatbotHealthy] = useState(false);
  const [suggestionTopicMsg, setSuggestionTopicMsg] = useState("");
  const [chatbotInfoStorage, setChatbotInfoStorage] = useState(
    () => {
      const store = JSON.parse(localStorage.getItem('chatbot_info_storage'));
      return store != null ? store : {
        sessionId: "",
        topicId: -1,
        resetMemory: false,
        messageList: [],
        isOpen: false
      };
    }
  );

  // notify to sync chatbot message on other tabs
  useEffect(() => {
    resetChatbotChannel.onmessage = ev => {
      //console.log("receive reset chatbot message", ev.data);
      setChatbotInfoStorage(ev.data);
    };

    return () => {
      resetChatbotChannel.close();
    };
  }, [resetChatbotChannel]);

  // init to open chatbot window
  useEffect(() => {

    if (chatbotInfoStorage.isOpen === true) {
      setShow(true);
    }
  }, []);

  useEffect(() => {
    if (suggestionInView === false) {
      setShowSuggestion(false);
    }

    if (suggestionInView === true && closeSuggestionByUser === false) {
      setShowSuggestion(true);
    }

  }, [suggestionInView]);

  useEffect(() => {
    chatbotHealthCheck()
      .then((res) => {
        console.log("chatbotHealthCheck", res);
        // chatbot is healthy
        if (res?.data?.status === 1 && res?.data?.globalErrorMessageList?.length === 0) {
          setIsChatbotHealthy(true);
        }
      }).catch((error) => {
        setIsChatbotHealthy(false);
      })
  }, [])

  const handleShow = (show) => {
    //console.log("handle show chatbot", chatbotInfoStorage, chatbotInfoStorage.sessionId);
    // toggele show/hide chat window 
    let newChatbotInfoStorage = { ...chatbotInfoStorage, isOpen: !show };

    setShow(!show);
    // hide suggestion topics
    setShowSuggestion(false);

    if (chatbotInfoStorage.sessionId === "") {
      newChatbotInfoStorage = {
        ...newChatbotInfoStorage,
        sessionId: crypto.randomUUID(),
        topicId: -1
      }
    }

    if (!show === true) {
      if (chatbotInfoStorage.messageList != null && chatbotInfoStorage.messageList.length === 0) {
        //WELCOME_MESSAGE.timestamp = buildTimestamp();
        newChatbotInfoStorage = {
          ...newChatbotInfoStorage,
          messageList: [
            WELCOME_MESSAGE
          ]
        }
      }
    }

    setChatbotInfoStorage(newChatbotInfoStorage);
    localStorage.setItem('chatbot_info_storage', JSON.stringify(newChatbotInfoStorage));
  }

  const handleClose = () => {
    // close the chat window, hide suggestion messages, clean chatbot session and message list
    setShow(false);

    WELCOME_MESSAGE.timestamp = buildTimestamp();
    const newChatbotInfoStorage = {
      ...chatbotInfoStorage,
      sessionId: "",
      messageList: [WELCOME_MESSAGE],
      isOpen: false
    }

    setChatbotInfoStorage(newChatbotInfoStorage);
    localStorage.setItem('chatbot_info_storage', JSON.stringify(newChatbotInfoStorage));
    resetChatbotChannel.postMessage(newChatbotInfoStorage);
  }

  // only close the chatbot window
  const handleMinimize = () => {
    setShow(false);
    //dispatch(setIsOpen(false));

  }

  const handleSuggestionTopic = (msg) => {
    // set the suggestion topic, uuid if there is no uuid and trigger the chatbot window

    let newChatbotInfoStorage = { ...chatbotInfoStorage };
    if (chatbotInfoStorage.sessionId === "") {
      newChatbotInfoStorage = {
        ...chatbotInfoStorage,
        sessionId: crypto.randomUUID()
      }
    }

    // question example:
    // {
    //   "message": "what is DE55",
    //   "reset_memory": true / false,
    //   "session_id": "UI_generated_session_id",
    //   "timestamp": "Feb 11, 01:36 PM"
    // }
    WELCOME_MESSAGE.timestamp = buildTimestamp();

    setSuggestionTopicMsg(msg);
    setShow(true);
    setShowSuggestion(false);

    newChatbotInfoStorage = {
      ...newChatbotInfoStorage,
      isOpen: true,
      messageList: [
        WELCOME_MESSAGE,
      ]
    }

    setChatbotInfoStorage(newChatbotInfoStorage);
    localStorage.setItem('chatbot_info_storage', JSON.stringify(newChatbotInfoStorage));
    resetChatbotChannel.postMessage(newChatbotInfoStorage);
  }

  const handleCloseSuggestion = () => {
    setCloseSuggestionByUser(true);
    setShowSuggestion(false);
  }

  const trigger = (
    <>
      {/* suggestion topics */}
      {showSuggestion ?
        <div
          className="chatbot-suggestion-container"
          style={{
            bottom: SUGGESTION_CONTAIONER_BOTTOM,
            zIndex,
          }}>
          <div className="welcome-message mb-3 fade-in">
            <div className="">
              <Button
                className="close-btn"
                onClick={handleCloseSuggestion}
              >
                <CloseIcon />
              </Button>
            </div>
            <div className="d-flex">
              <div>
                <Image
                  className="me-2"
                  alt="PSOPPC Logo"
                  src={Logo}
                  width={"40px"}
                  height={"40px"}
                />
              </div>
              <div className="">
                Hi there 👋 Welcome to PSOPPC! How can we help you?
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-end fade-in">
            <Button
              className="suggestion-topic mb-3"
              onClick={() => handleSuggestionTopic(SUGGESTION_MESSAGE_LIST[0])}
            >
              How do I become a PSO?
            </Button>
          </div>
          <div className="d-flex justify-content-end fade-in">
            <Button
              className="suggestion-topic mb-3"
              onClick={() => handleSuggestionTopic(SUGGESTION_MESSAGE_LIST[1])}
            >
              What is AHRQ Common Formats?
            </Button>
          </div>
          <div className="d-flex justify-content-end fade-in">
            <Button
              className="suggestion-topic mb-3"
              onClick={() => handleSuggestionTopic(SUGGESTION_MESSAGE_LIST[2])}
            >
              How do I submit patient safety data?
            </Button>
          </div>
        </div> : null}
      {/* chatbot icon trigger */}
      <IconButton
        title="PSOPPC Chatbot"
        variant="primary"
        hiddenText="Open Chatbot"
        icon={(show != true ? ICONS.chatbotNotification : ICONS.chatbot)}
        className={"chatbot-trigger-container"}
        onClick={() => handleShow(show)}
      ></IconButton>
    </>
  );

  return (
    <>
      {isChatbotHealthy && createPortal(trigger, document.body)}
      {createPortal(
        <ChatbotWindow
          isOpen={show}
          handleClose={handleClose}
          handleMinimize={handleMinimize}
          resetChatbotChannel={resetChatbotChannel}
          suggestionTopicMsg={suggestionTopicMsg}
          chatbotInfoStorage={chatbotInfoStorage}
          setChatbotInfoStorage={setChatbotInfoStorage}
        >
        </ChatbotWindow>, document.body)}
    </>
  )
}

export default Chatbot;